import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import GoogleMap from 'google-map-react';
import { useCallback, useState, useRef, useEffect, Children } from 'react';
import Icon from '../Icon/Icon.js';
import Marker from './Marker.js';
import { globeMapStyles } from './mapStyles.js';
import { GOOGLE_MAP_API_KEY } from './config.js';
import { MapRootContainer, CloseButton } from './style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useGlobals from '../../hooks/useGlobals.js';
import { ICON_UI_CLOSE } from '../../icons/ICON_UI_CLOSE.js';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var getMapLanguage = function getMapLanguage(siteId) {
  switch (siteId) {
    case 3:
      return 'no';
    case 11:
      return 'da';
    case 15:
      return 'fi';
    default:
      return 'sv';
  }
};

/**
 * Creates map options for initializing a Google Map instance.
 *
 * @param {object} maps - The Google Maps API object.
 * @param {string} mapId - The ID of the map to be used.
 * @param {number} siteId - The ID of the site to determine specific map styles.
 * @returns {object} The options object for initializing the map.
 */
var createMapOptions = function createMapOptions(maps, mapId, siteId) {
  return _objectSpread({
    fullscreenControl: false,
    scaleControl: true,
    mapTypeControl: true,
    mapTypeControlOptions: {
      style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
      mapTypeIds: ['roadmap', 'satellite']
    },
    mapId: mapId
  }, siteId === 18 && {
    styles: globeMapStyles
  });
};
var filterReactChildren = function filterReactChildren(children, conditionFn) {
  return Children.map(children, function (child) {
    if (!child) {
      return null;
    }
    return conditionFn(child) ? child : null;
  });
};
var boundsEqual = function boundsEqual(bounds1, bounds2) {
  var _bounds1$sw, _bounds2$sw, _bounds1$sw2, _bounds2$sw2, _bounds1$ne, _bounds2$ne, _bounds1$ne2, _bounds2$ne2;
  return (bounds1 === null || bounds1 === void 0 || (_bounds1$sw = bounds1.sw) === null || _bounds1$sw === void 0 ? void 0 : _bounds1$sw.lat) === (bounds2 === null || bounds2 === void 0 || (_bounds2$sw = bounds2.sw) === null || _bounds2$sw === void 0 ? void 0 : _bounds2$sw.lat) && (bounds1 === null || bounds1 === void 0 || (_bounds1$sw2 = bounds1.sw) === null || _bounds1$sw2 === void 0 ? void 0 : _bounds1$sw2.lng) === (bounds2 === null || bounds2 === void 0 || (_bounds2$sw2 = bounds2.sw) === null || _bounds2$sw2 === void 0 ? void 0 : _bounds2$sw2.lng) && (bounds1 === null || bounds1 === void 0 || (_bounds1$ne = bounds1.ne) === null || _bounds1$ne === void 0 ? void 0 : _bounds1$ne.lat) === (bounds2 === null || bounds2 === void 0 || (_bounds2$ne = bounds2.ne) === null || _bounds2$ne === void 0 ? void 0 : _bounds2$ne.lat) && (bounds1 === null || bounds1 === void 0 || (_bounds1$ne2 = bounds1.ne) === null || _bounds1$ne2 === void 0 ? void 0 : _bounds1$ne2.lng) === (bounds2 === null || bounds2 === void 0 || (_bounds2$ne2 = bounds2.ne) === null || _bounds2$ne2 === void 0 ? void 0 : _bounds2$ne2.lng);
};

/**
 * Map component that renders a Google Map with markers and other children components.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.center - The center coordinates of the map.
 * @param {number} props.center.lat - The latitude of the center.
 * @param {number} props.center.lng - The longitude of the center.
 * @param {string} props.channel - The channel for the Google Maps API.
 * @param {React.ReactNode} [props.children] - The child components to render inside the map.
 * @param {string} [props.className] - Additional class names for the map container.
 * @param {Function} [props.onBoundsChange] - Callback function to handle bounds change events.
 * @param {Function} [props.onClose] - Callback function to handle the close button click event.
 * @param {Function} [props.onZoomChange] - Callback function to handle zoom change events.
 * @param {number} [props.zoom=12] - The initial zoom level of the map.
 * @param {string} [props.mapId] - The ID of the map.
 *
 * @returns {JSX.Element|null} The rendered map component or null if channel or center is not provided.
 */
var Map = function Map(_ref) {
  var center = _ref.center,
    channel = _ref.channel,
    _ref$children = _ref.children,
    children = _ref$children === void 0 ? undefined : _ref$children,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? undefined : _ref$className,
    _ref$onBoundsChange = _ref.onBoundsChange,
    onBoundsChange = _ref$onBoundsChange === void 0 ? undefined : _ref$onBoundsChange,
    _ref$onClose = _ref.onClose,
    onClose = _ref$onClose === void 0 ? undefined : _ref$onClose,
    _ref$onZoomChange = _ref.onZoomChange,
    onZoomChange = _ref$onZoomChange === void 0 ? function () {} : _ref$onZoomChange,
    _ref$zoom = _ref.zoom,
    currentZoom = _ref$zoom === void 0 ? 12 : _ref$zoom,
    _ref$mapId = _ref.mapId,
    mapId = _ref$mapId === void 0 ? undefined : _ref$mapId;
  var _useGlobals = useGlobals(),
    siteId = _useGlobals.siteId;
  var distanceToMouse = useCallback(function (_ref2, _ref3) {
    var x = _ref2.x,
      y = _ref2.y;
    var mouseX = _ref3.x,
      mouseY = _ref3.y;
    var offsetY = mouseY + 18;
    return Math.sqrt((x - mouseX) * (x - mouseX) + 0.8 * (y - offsetY) * (0.8 * (y - offsetY)));
  }, []);
  var _useState = useState(currentZoom),
    _useState2 = _slicedToArray(_useState, 2),
    zoom = _useState2[0],
    setZoom = _useState2[1];
  var bounds = useRef({});
  useEffect(function () {
    setZoom(currentZoom);
  }, [currentZoom]);
  var handleChange = useCallback(function (changeEvent) {
    if (changeEvent.zoom !== zoom) {
      setZoom(changeEvent.zoom);
      onZoomChange({
        zoomLevel: changeEvent.zoom
      });
    }
    if (onBoundsChange && !boundsEqual(changeEvent.bounds, bounds.current)) {
      bounds.current = changeEvent.bounds;
      onBoundsChange({
        bounds: changeEvent.bounds
      });
    }
  }, [onZoomChange, zoom, setZoom, onBoundsChange]);
  if (!channel || !center) {
    return null;
  }
  return /*#__PURE__*/jsxs(MapRootContainer, {
    className: className,
    children: [onClose && /*#__PURE__*/jsx(CloseButton, {
      className: "gtm-list-view wdio-close-map",
      onClick: function onClick(event) {
        return onClose(event);
      },
      type: "button",
      children: /*#__PURE__*/jsx(Icon, {
        path: ICON_UI_CLOSE,
        size: "xs"
      })
    }), /*#__PURE__*/jsx(GoogleMap, {
      bootstrapURLKeys: {
        key: GOOGLE_MAP_API_KEY,
        language: getMapLanguage(siteId),
        channel: channel
      },
      center: center,
      zoom: zoom,
      options: function options(maps) {
        return createMapOptions(maps, mapId, siteId);
      },
      hoverDistance: 20,
      distanceToMouse: distanceToMouse,
      onChange: handleChange,
      children: filterReactChildren(children, function (child) {
        return child.type === Marker;
      })
    }), filterReactChildren(children, function (child) {
      return child.type !== Marker;
    })]
  });
};
var Map$1 = Map;

export { Map$1 as default };
//# sourceMappingURL=Map.js.map
